import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { RbacService } from '../services/rbac.service';
import { AppModule } from '../rbac/rbac.policy';

/**
 * Use with route data: `{ module: AppModule }` or `{ roles: OfficerRole[] }`.
 * Prefer `module` so policy stays centralized in rbac.policy.ts.
 */
export const roleGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  const rbac = inject(RbacService);
  const router = inject(Router);

  // authGuard sits on the parent shell route, which the router reuses across
  // child navigations, so it never re-runs once the session is dropped. Without
  // this check a signed-out user falls through to the deny branch below and
  // lands on an unusable dashboard reporting `role (unknown)`.
  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login'], { queryParams: { reason: 'session' } });
  }

  const module = route.data['module'] as AppModule | undefined;
  if (module) {
    if (rbac.canModule(module)) return true;
    // Soft landing on home — no scary “Access denied” banner for modules this
    // role simply does not use (e.g. Records Officer → notifications removed).
    return router.createUrlTree(['/dashboard']);
  }

  const roles = route.data['roles'] as string[] | undefined;
  if (roles?.length) {
    const role = rbac.role();
    if (role && roles.includes(role)) return true;
    return router.createUrlTree(['/dashboard']);
  }

  return true;
};
